"use client";
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, type Ref } from "react";
import snakeCase from "lodash/snakeCase";

import {
  StyledLink,
  StyledButton,
  type StyledLinkProps,
  type StyledButtonProps,
} from "@stadium/ui/src/components/button/styled-button";

import ExternalButton, {
  type ExternalButtonProps,
} from "@stadium/ui/src/components/button/external-button";

import useRouter from "@stadium/hooks/lib/use-custom-route";

import { getWorkspaceUrl } from "@stadium/helpers/routes";

import { usePreviousRouteContext } from "@stadium/hooks/lib/use-previous-route";

import {
  useJuneSoEventTracking,
  TrackEventArgumentsTypes,
} from "@stadium/services/june-analytics/hooks/use-june-so-event-tracking";
import { useWithAnalytics } from "@stadium/services/analytics/hooks/use-with-analytics";

import { DEFAULT_SERVICES } from "@stadium/shared/src/constants/june-so";
import {
  LOGIN_CALLBACK_ROUTE as SERVICES_LOGIN_CALLBACK_ROUTE,
  LOGOUT_CALLBACK_ROUTE,
  PROXY_LOGOUT_CALLBACK_ROUTE,
} from "@stadium/services/auth0/constants";

import styles from "./main.module.scss";

type JuneSoPropsTypes = {
  services?: readonly string[];
} & TrackEventArgumentsTypes;

/** Mixpanel segment events when wrapped in `EventLocationProvider` (same pattern as shops `InternalButton`). */
export type EventLocationAnalyticsConfig = {
  eventProperties?: Record<string, unknown>;
  target?: string;
  /** Segment after location/target; defaults to the shared `ANALYTICS_EVENT_ACTION_CLICKED` constant. */
  action?: string;
  track?: boolean;
};

const BASE_BACK_BUTTON_SKIPPED_ROUTES = [
  LOGOUT_CALLBACK_ROUTE,
  PROXY_LOGOUT_CALLBACK_ROUTE,
  SERVICES_LOGIN_CALLBACK_ROUTE,
];

export type ButtonProps = {
  juneSoProps?: JuneSoPropsTypes;
  eventLocationAnalytics?: EventLocationAnalyticsConfig;
} & StyledButtonProps;

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ juneSoProps, eventLocationAnalytics, onClick, ...buttonProps }: ButtonProps, ref) => {
    const { services = DEFAULT_SERVICES, ...juneSoEventProps } = juneSoProps || {};

    const { trackEvent } = useJuneSoEventTracking({ services });
    const withAnalytics = useWithAnalytics();

    const handleClick = (args: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      onClick?.(args);
      trackEvent({ ...juneSoEventProps });
      const fallbackTarget = snakeCase(args?.currentTarget?.textContent ?? "");
      withAnalytics(undefined, {
        ...eventLocationAnalytics,
        target: eventLocationAnalytics?.target ?? fallbackTarget,
      })();
    };

    return (
      <StyledButton
        displayChildren
        styles={styles}
        disableOnLoad
        {...buttonProps}
        onClick={handleClick}
        ref={ref}
      />
    );
  },
);

export const WorkspaceLinkButton = ({
  juneSoProps,
  href,
  prependWorkspaceSlug = true,
  ...buttonProps
}: { juneSoProps?: JuneSoPropsTypes; prependWorkspaceSlug?: boolean } & StyledLinkProps) => {
  const router = useRouter();
  const { workspaceSlug } = router.query;

  const { services = DEFAULT_SERVICES, ...juneSoEventProps } = juneSoProps || {};

  const { trackEvent } = useJuneSoEventTracking({ services });

  const onClick = (args: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    buttonProps.onClick?.(args);
    trackEvent({ ...juneSoEventProps });
  };

  return (
    <StyledLink
      href={getWorkspaceUrl({
        url: href,
        workspaceSlug,
        prependWorkspaceSlug,
        pathname: router.asPath,
      })}
      displayChildren
      disableOnLoad
      styles={styles}
      prefetch={false}
      {...buttonProps}
      onClick={onClick}
    />
  );
};

export type LinkButtonProps = {
  juneSoProps?: JuneSoPropsTypes;
  eventLocationAnalytics?: EventLocationAnalyticsConfig;
} & StyledLinkProps;

export const LinkButton = ({
  juneSoProps,
  eventLocationAnalytics,
  ...buttonProps
}: LinkButtonProps) => {
  const { services = DEFAULT_SERVICES, ...juneSoEventProps } = juneSoProps || {};

  const { trackEvent } = useJuneSoEventTracking({ services });
  const withAnalytics = useWithAnalytics();

  const onClick = (args: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    buttonProps.onClick?.(args);
    trackEvent({ ...juneSoEventProps });
    const fallbackTarget = snakeCase(args?.currentTarget?.textContent ?? "");
    withAnalytics(undefined, {
      ...eventLocationAnalytics,
      target: eventLocationAnalytics?.target ?? fallbackTarget,
    })();
  };

  return (
    <StyledLink
      displayChildren
      disableOnLoad
      styles={styles}
      prefetch={false}
      {...buttonProps}
      onClick={onClick}
    />
  );
};

export const WorkspaceExternalLinkButton = forwardRef(
  (
    {
      juneSoProps,
      prependWorkspaceSlug = true,
      href,
      ...buttonProps
    }: { juneSoProps?: JuneSoPropsTypes; prependWorkspaceSlug?: boolean } & ExternalButtonProps,
    ref: Ref<HTMLAnchorElement>,
  ) => {
    const router = useRouter();
    const { workspaceSlug } = router.query;

    const { services = DEFAULT_SERVICES, ...juneSoEventProps } = juneSoProps || {};

    const { trackEvent } = useJuneSoEventTracking({ services });

    const onClick = (args: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      buttonProps.onClick?.(args);
      trackEvent({ ...juneSoEventProps });
    };

    return (
      <ExternalButton
        styles={styles}
        ref={ref}
        href={
          href && href !== "#"
            ? getWorkspaceUrl({
                url: href,
                workspaceSlug,
                prependWorkspaceSlug,
                pathname: router.asPath,
              })
            : "#"
        }
        {...buttonProps}
        onClick={onClick}
      />
    );
  },
);

export const ExternalLinkButton = forwardRef(
  (
    { juneSoProps, ...buttonProps }: { juneSoProps?: JuneSoPropsTypes } & ExternalButtonProps,
    ref: Ref<HTMLAnchorElement>,
  ) => {
    const { services = DEFAULT_SERVICES, ...juneSoEventProps } = juneSoProps || {};

    const { trackEvent } = useJuneSoEventTracking({ services });

    const onClick = (args: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      buttonProps.onClick?.(args);
      trackEvent({ ...juneSoEventProps });
    };

    return <ExternalButton styles={styles} ref={ref} {...buttonProps} onClick={onClick} />;
  },
);

export const defaultBackButtonAction: (
  e: Parameters<React.MouseEventHandler<HTMLAnchorElement>>[0],
) => boolean = (e) => {
  if (window.opener) {
    e.preventDefault();
    window.opener.focus();
    window.close();
    return true;
  }
  return false;
};

type BackButtonProps = StyledLinkProps & {
  baseClassName?: string;
  checkOpener?: boolean;
  skipRoutes?: string[];
  baseSkipRoutes?: string[];
  fallbackRoute?: string;
  icon?: boolean;
  appendQueryParams?: boolean;
  allowedRoutes?: string[];
};

export const BackButton = forwardRef<
  HTMLAnchorElement & { triggerBackAction: () => void },
  BackButtonProps
>(
  (
    {
      children,
      className = "",
      baseClassName = styles.backButton,
      checkOpener = true,
      onClick,
      skipRoutes,
      isLoading,
      fallbackRoute = "/",
      baseSkipRoutes = BASE_BACK_BUTTON_SKIPPED_ROUTES,
      icon = true,
      appendQueryParams = false,
      allowedRoutes,
      ...props
    },
    ref,
  ) => {
    const { history, getPreviousUrl } = usePreviousRouteContext();
    const { replace, push } = useRouter();
    const anchorRef = useRef<HTMLAnchorElement>(null);

    const href = useMemo(() => {
      const skippedRoutes = skipRoutes?.length
        ? [...skipRoutes, ...baseSkipRoutes]
        : baseSkipRoutes;
      const prevRoute = getPreviousUrl({ skippedRoutes, allowedRoutes });
      if (!prevRoute) {
        return fallbackRoute;
      }

      if (appendQueryParams) {
        return prevRoute.href;
      }
      return prevRoute.pathname;
    }, [
      skipRoutes,
      baseSkipRoutes,
      getPreviousUrl,
      appendQueryParams,
      fallbackRoute,
      allowedRoutes,
    ]);

    const defaultBackAction = useCallback(
      (e?: React.MouseEvent<HTMLAnchorElement>) => {
        if (checkOpener && history.length === 0 && defaultBackButtonAction(e)) {
          return true;
        }

        onClick?.(e);

        if (href != fallbackRoute) {
          e?.preventDefault();
          replace(href);
          return true;
        }

        return false;
      },
      [checkOpener, history.length, href, onClick, replace, fallbackRoute],
    );

    useImperativeHandle(
      ref,
      () => ({
        ...anchorRef.current,
        triggerBackAction: () => {
          if (!defaultBackAction()) {
            push(href);
          }
        },
        defaultBackAction,
      }),
      [defaultBackAction, href, push],
    );

    return (
      <StyledLink
        href={href}
        className={`${className} ${baseClassName}`}
        onClick={defaultBackAction}
        ref={anchorRef}
        styles={styles}
        disabled={isLoading}
        {...props}
      >
        {icon && <span className={styles.backIcon}></span>}
        {children ?? "BACK TO MENU"}
      </StyledLink>
    );
  },
);

export default Button;
